import React from 'react';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { showCreateListingLinkForUser, showPaymentDetailsForUser } from '../../util/userHelpers';

import {
  Sidebar,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '../ui/sidebar';
import { Separator } from '../ui/separator';

import AppShellNav from './AppShellNav';
import css from './AppShell.module.css';

import '../../styles/tailwind.css';

interface AppShellProps {
  currentPage: string;
  currentUser?: any;
  children: React.ReactNode;
}

/**
 * AppShell — unified dashboard layout with collapsible sidebar navigation.
 *
 * Sits inside a LayoutSingleColumn/LayoutSideNavigation Main area, below the
 * marketplace topbar. CSS module overrides make the sidebar sticky within
 * the content area rather than fixed to the full viewport.
 */
const AppShellComponent: React.FC<AppShellProps> = ({ currentPage, currentUser, children }) => {
  const config = useConfiguration();
  const showManageListingsLink = showCreateListingLinkForUser(config, currentUser);
  const { showPayoutDetails, showPaymentMethods } = showPaymentDetailsForUser(config, currentUser);
  const userType = currentUser?.attributes?.profile?.publicData?.userType;

  return (
    <div className={css.root}>
      <SidebarProvider className="!min-h-0 h-full" style={{ '--sidebar-width': '20rem' } as React.CSSProperties}>
        <Sidebar collapsible="icon">
          <SidebarHeader className="h-2" />
          <AppShellNav
            currentPage={currentPage}
            userType={userType}
            showManageListingsLink={showManageListingsLink}
            showPaymentMethods={showPaymentMethods}
            showPayoutDetails={showPayoutDetails}
          />
        </Sidebar>
        <SidebarInset className="min-h-0">
          <header className="flex h-10 shrink-0 items-center gap-2 border-b px-4 md:hidden">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
          </header>
          <div className="flex-1 overflow-auto p-4 pb-12 md:p-6 md:pb-16">{children}</div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  currentUser: state.user.currentUser,
});

const AppShell = connect(mapStateToProps)(AppShellComponent);

export default AppShell;
